import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import {
  Grid, TextField, FormGroup, Paper, Tooltip,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import * as Yup from 'yup';
import Progress from '../../components/Progress';
import { useDispatch, useSelector } from 'react-redux';
import { setNameUser } from '../../redux/nameUser';
import { setDisable } from '../../redux/disable';
import { setLoading } from '../../redux/loading';
import Success from '../../components/Snack/Success';
import Error from '../../components/Snack/Error';
import maskCPF from 'components/Masks/maskCPF';
import { LINK_TO_PRIVACY_POLICY, contactUsPhone } from 'utils/consts';
import styles from './styles.module.css';
import styleGlobal from '../../Global.module.css';
import InfoIcon from '@mui/icons-material/Info';
import UserService from 'services/UserService';

function EditUser() {
  const history = useHistory();
  const [openProgress, setOpenProgress] = React.useState(false);
  const dispatch = useDispatch();
  const notIsFirstAccess = useSelector(
    (state) => state.notIsFirstAccess.notIsFirstAccess,
  );
  const error = Error();
  const success = Success();

  useEffect(() => {
    // carrega os dados no seu devido campo caso tenha id, do contrário,
    // carrega o formulário vazio para cadastrar um novo
    async function load() {
      setOpenProgress(true);
      const user = await UserService.currentUser();

      const splitName = user.name.split(' ');
      const lastName = splitName.slice(1).join(' ');

      formik.setValues({
        ...user,
        cpf: user.cpf_cnpj,
        first_name: splitName[0],
        last_name: lastName,
      });
      setOpenProgress(false);
    }
    load();
  }, []);

  async function request(data) {
    // envia os dados para api salvar no banco
    dispatch(setDisable(true));
    try {
      await api.put(`/users/${data.id}`, data).then((res) => {
        success();
      });
      localStorage.setItem('username', data.first_name);
      dispatch(setNameUser(data.first_name));
      history.push(!notIsFirstAccess ? '/first-property' : '/property');
    } catch (err) {
      error();
    }
    dispatch(setDisable(false));
    dispatch(setLoading(false));
  }

  // Criação dos campos e validação para o formulário
  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      cpf: '',
    },
    validationSchema: Yup.object({
      first_name: Yup.string().required('Campo Obrigatório').nullable(),
      last_name: Yup.string().required('Campo Obrigatório').nullable(),
      email: Yup.string()
        .required('Campo Obrigatório')
        .email('E-mail inválido'),
      phone: Yup.string().required('Campo Obrigatório').nullable(),
      cpf: Yup.string()
        .matches(
          /^[0-9]{3}[.]?[0-9]{3}[.]?[0-9]{3}[-]?[0-9]{2}$/,
          'O número digitado não é válido.',
        )
        .required('Campo Obrigatório'),
      password: Yup.string()
        .matches(
          /^((?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]))/,
          'A senha deve conter ao menos uma letra minúscula, uma letra maiúscula e um número',
        )
        .min(6, 'A senha deve conter ao menos 6 caracteres')
        .required('Campo Obrigatório'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'As senhas devem ser iguais!')
        .required('Campo Obrigatório'),
    }),
    onSubmit: (values) => {
      dispatch(setLoading(true));
      request(values);
    },
  });

  // Estilização do tooltip
  const HtmlTooltip = withStyles((theme) => ({
    tooltip: {
      backgroundColor: '#FFFFFF',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #0367bf',
    },
  }))(Tooltip);

  return (
    <header
      className={styleGlobal.headerGlobal}
      component="form"
      onSubmit={formik.handleSubmit}
    >
      {openProgress ? <Progress /> : null}
      <br />
      <Paper style={{ padding: '2rem', borderRadius: '20px' }}>
        <h2 style={{ marginBottom: '2rem' }}>
          Dados da Minha Conta
          {' '}
          <HtmlTooltip
            title={(
              <>
                Esses são seus dados pessoais inseridos no momento do cadastro. Caso queira atualizá-los, é necessário entrar em contato com a nossa equipe de suporte técnico
              </>
            )}
            placement="right"
            arrow
            interactive
          >
            <InfoIcon color="primary" fontSize="inherit" />
          </HtmlTooltip>
        </h2>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormGroup>
              <TextField
                id="formGridName"
                label="Nome"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                name="first_name"
                placeholder="Nome"
                type="text"
                value={formik.values.first_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.first_name && formik.errors.first_name}
                helperText={
                  formik.touched.first_name && formik.errors.first_name
                }
                disabled
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormGroup>
              <TextField
                id="formGridName"
                label="Sobrenome"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                name="last_name"
                placeholder="Sobrenome"
                type="text"
                value={formik.values.last_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.last_name && formik.errors.last_name}
                helperText={formik.touched.last_name && formik.errors.last_name}
                disabled
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormGroup>
              <TextField
                id="formGridEmail"
                label="Email"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                name="email"
                placeholder="Endereço de e-mail"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
                helperText={formik.touched.email && formik.errors.email}
                disabled
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormGroup>
              <TextField
                id="phone"
                label="Telefone"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                name="phone"
                placeholder="Telefone"
                type="phone"
                value={formik.values.phone || 'Sem número de telefone'}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && formik.errors.phone}
                helperText={formik.touched.phone && formik.errors.phone}
                disabled
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormGroup>
              <TextField
                id="cpf"
                label="CPF"
                placeholder="CPF"
                name="cpf"
                variant="outlined"
                InputLabelProps={{
                  style: { pointerEvents: 'auto' },
                  shrink: true,
                }}
                value={formik.values.cpf}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                InputProps={
                  maskCPF && {
                    inputComponent: maskCPF,
                  }
                }
                error={formik.touched.cpf && formik.errors.cpf}
                helperText={formik.touched.cpf && formik.errors.cpf}
                disabled
              />
            </FormGroup>
          </Grid>
          {/* <Grid
            item
            container
            direction="row"
            justify="space-between"
            alignItems="flex-end"
            className={classes.root}
            xs={12}
          >
            <Grid item xs>
              <ReturnButton
                onClick={() => {
                  history.push("/dashboards");
                }}
                disabled={disable}
              />
            </Grid>

            <Grid item xs>
              <SaveButton
                disabled={disable}
                onClick={() => request(formik.values)}
              />
            </Grid>
          </Grid> */}
          <Grid item xs={12}>
            <a href={LINK_TO_PRIVACY_POLICY} download="Politica_de_privacidade_e_segurança_Greenfy.pdf" target="_blank" className={styles.link} rel="noreferrer">
              Acessar Política de Privacidade e Segurança
            </a>
          </Grid>
          <Grid item xs={12}>
            <a
              href={`https://api.whatsapp.com/send?phone=${contactUsPhone}&text=Oi%2C%20preciso%20de%20ajuda!`}
              target="_blank"
              className={styles.link}
              rel="noreferrer"
            >
              Entrar em Contato com a Equipe de Suporte Técnico
            </a>
          </Grid>
        </Grid>
      </Paper>
    </header>
  );
}

export default EditUser;
